import { Component, ViewEncapsulation, computed, inject, linkedSignal, signal } from '@angular/core'
import { FormField, disabled, form } from '@angular/forms/signals'

import { omit as _omit } from 'lodash'

import { FlxButtonModule, FlxFormControlModule, FlxModalModule } from '@flowx/angular-ui-toolkit'
import { parseColourValueOr } from '@flowx/core-theme'
import { FlxColourSwatchDirective } from '@flowx/designer/shared/directives/colour-swatch.directive'

import { FlxColorsShadesEditorComponent } from './colors-shades-editor/colors-shades-editor.component'

import { THEME_READ_ONLY } from '../../../services/theme-read-only.token'
import { ColorForm, MAIN_COLOR_SHADE } from '../color.entity'
import { ThemeColorShades, getShadesFormHex, getThemeColorFromShades } from '../color.helpers'
import { FlxThemeColorService } from '../color.service'

/** Main colour drafts by colour path. */
type MainColourDrafts = Record<string, string>

@Component({
  selector: 'flx-theme-colors-form',
  imports: [
    FormField,
    FlxFormControlModule,
    FlxButtonModule,
    FlxColorsShadesEditorComponent,
    FlxModalModule,
    FlxColourSwatchDirective,
  ],
  templateUrl: './theme-colors-form.component.html',
  styleUrls: ['./theme-colors-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'flx-theme-colors-form',
  },
})
export class FlxThemeColorsFormComponent {
  readonly #colorService = inject(FlxThemeColorService)
  readonly #selectedColorPath = signal<string | null>(null)

  /** What is typed in each main colour field; it reaches the theme only on blur or Enter. */
  readonly #mainColourDrafts = linkedSignal<MainColourDrafts>(() =>
    Object.fromEntries(
      (this.#colorService.formData() ?? []).map((color) => [
        color.path,
        color.fg[MAIN_COLOR_SHADE] ?? '',
      ])
    )
  )

  readonly readOnly = inject(THEME_READ_ONLY)
  readonly colors = this.#colorService.formData
  readonly mainColourForm = form(this.#mainColourDrafts, (path) =>
    disabled(path, { when: () => this.readOnly() })
  )

  /** The colour whose shades are being edited, with its current values. */
  readonly selectedColor = computed<ColorForm | null>(() => {
    const path = this.#selectedColorPath()
    const color = this.colors()?.find((item) => item.path === path)
    return color ?? null
  })

  selectColor(path: string | null): void {
    this.#selectedColorPath.set(path)
  }

  /** Stores a valid typed main colour, normalised, with regenerated shades; restores an invalid one. */
  commitMainColour(color: ColorForm): void {
    const draftField = this.mainColourForm[color.path]
    const storedColour = color.fg[MAIN_COLOR_SHADE]
    const committedColour = parseColourValueOr(draftField().value(), storedColour)
    // reset also rewrites the text field when the restored value is the one it showed last
    draftField().reset(committedColour)
    if (committedColour !== storedColour) {
      this.#colorService.updateData(this.#withGeneratedShades(color, committedColour))
    }
  }

  updateShades(newShades: Partial<ThemeColorShades>, color: ColorForm): void {
    this.#colorService.updateData({ ...color, fg: { ...color.fg, ...newShades } })
  }

  /** Regenerates every shade from a new main (500) shade, keeping the main value as given. */
  #withGeneratedShades(color: ColorForm, mainColour: string): ColorForm {
    const generated = _omit(getThemeColorFromShades(getShadesFormHex(mainColour)), [
      MAIN_COLOR_SHADE,
    ])
    return { ...color, fg: { ...color.fg, ...generated, [MAIN_COLOR_SHADE]: mainColour } }
  }
}
